import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  listBriefs, updateBrief, type BriefStatus,
  listBriefReplies, createBriefReply, type BriefReply,
  searchPhotographersForReply, getPhotographersByIds, type PhotographerPick,
} from "@/lib/admin-api";
import { sendTransactionalEmail } from "@/lib/email/send";

export const Route = createFileRoute("/admin/briefs")({
  component: BriefsPage,
});

type Brief = Awaited<ReturnType<typeof listBriefs>>[number];

const statusLabels: Record<BriefStatus | "all", string> = {
  all: "Alle",
  new: "Neu",
  in_review: "In Prüfung",
  published: "Veröffentlicht",
  closed: "Geschlossen",
  rejected: "Rejected",
};

const allStatuses: BriefStatus[] = ["new", "in_review", "published", "closed", "rejected"];

function BriefsPage() {
  const [filter, setFilter] = useState<BriefStatus | "all">("new");
  const [items, setItems] = useState<Brief[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Brief | null>(null);

  async function load() {
    setLoading(true);
    try { setItems(await listBriefs(filter)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Moderation</div>
          <h1 className="serif text-4xl md:text-5xl mt-3">Briefings</h1>
        </div>
        <div className="flex gap-2 eyebrow flex-wrap">
          {(Object.keys(statusLabels) as Array<BriefStatus | "all">).map((k) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1 border ${filter === k ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {statusLabels[k]}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 border border-border divide-y divide-border">
        {loading && <div className="p-6 text-sm text-muted-foreground">Lädt …</div>}
        {!loading && items.length === 0 && <div className="p-6 text-sm text-muted-foreground">Keine Einträge.</div>}
        {items.map((b) => (
          <button key={b.id} onClick={() => setOpen(b)} className="w-full text-left p-4 md:p-5 hover:bg-muted/30 grid grid-cols-12 gap-3 items-center">
            <div className="col-span-12 md:col-span-5">
              <div className="serif text-lg">{b.occasion ?? "Brief"}</div>
              <div className="text-xs text-muted-foreground line-clamp-1">{b.description}</div>
            </div>
            <div className="col-span-6 md:col-span-2 text-sm text-muted-foreground">{b.region ?? ""}</div>
            <div className="col-span-6 md:col-span-3 text-sm text-muted-foreground">{b.budget_range ?? ""}</div>
            <div className="col-span-12 md:col-span-2 text-right">
              <span className="eyebrow border border-border px-2 py-1">{b.status}</span>
            </div>
          </button>
        ))}
      </div>

      {open && <BriefDrawer item={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function BriefDrawer({ item, onClose, onSaved }: { item: Brief; onClose: () => void; onSaved: () => void }) {
  const [note, setNote] = useState(item.admin_note ?? "");
  const [status, setStatus] = useState<BriefStatus>(item.status as BriefStatus);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<"details" | "reply">("details");
  const [replies, setReplies] = useState<BriefReply[]>([]);
  useEffect(() => { listBriefReplies(item.id).then(setReplies).catch(() => {}); }, [item.id]);
  async function save() {
    setBusy(true);
    try { await updateBrief(item.id, status, note || null); onSaved(); }
    finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-background border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 md:p-8">
        <div className="flex justify-between items-start gap-4">
          <div>
            <div className="eyebrow text-muted-foreground">Briefing</div>
            <h2 className="serif text-3xl mt-2">{item.occasion ?? "Brief"}</h2>
            <div className="text-sm text-muted-foreground mt-1">Kontakt: {item.contact_name ? `${item.contact_name} · ` : ""}{item.contact_email}</div>
          </div>
          <button onClick={onClose} className="eyebrow">Close</button>
        </div>
        <div className="mt-6 flex gap-2 eyebrow border-b border-border">
          <button onClick={() => setTab("details")} className={`px-3 py-2 ${tab === "details" ? "border-b-2 border-foreground -mb-px" : "text-muted-foreground"}`}>Details</button>
          <button onClick={() => setTab("reply")} className={`px-3 py-2 ${tab === "reply" ? "border-b-2 border-foreground -mb-px" : "text-muted-foreground"}`}>Antwort senden{replies.length > 0 ? ` (${replies.length})` : ""}</button>
        </div>
        {tab === "details" ? (
        <>
        <dl className="mt-6 space-y-3 text-sm">
          <Row label="Thema">{item.theme ?? ""}</Row>
          <Row label="Region">{item.region ?? ""}</Row>
          <Row label="Budget">{item.budget_range ?? ""}</Row>
          <Row label="Zeitraum">{item.date_window ?? ""}</Row>
          <Row label="Beschreibung"><div className="whitespace-pre-wrap">{item.description}</div></Row>
          <Row label="Eingereicht">{new Date(item.created_at).toLocaleString()}</Row>
        </dl>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <label className="block">
            <div className="eyebrow text-muted-foreground mb-2">Status</div>
            <select value={status} onChange={(e) => setStatus(e.target.value as BriefStatus)}
              className="w-full border-b border-foreground bg-transparent py-2 outline-none">
              {allStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <div className="mt-4">
          <div className="eyebrow text-muted-foreground mb-2">Notiz (intern)</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            className="w-full border border-border bg-transparent p-3 text-sm" maxLength={2000} />
        </div>
        <div className="mt-6">
          <button disabled={busy} onClick={save} className="eyebrow bg-foreground text-background px-4 py-2 disabled:opacity-50">
            {busy ? "…" : "Speichern"}
          </button>
        </div>
        </>
        ) : (
          <ReplyComposer brief={item} replies={replies} onSent={async () => {
            setReplies(await listBriefReplies(item.id));
            // bump status to in_review on first reply
            if (item.status === "new") {
              await updateBrief(item.id, "in_review", item.admin_note);
            }
          }} />
        )}
      </div>
    </div>
  );
}

function ReplyComposer({ brief, replies, onSent }: { brief: Brief; replies: BriefReply[]; onSent: () => void | Promise<void> }) {
  const [body, setBody] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [picked, setPicked] = useState<PhotographerPick[]>([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<PhotographerPick[]>([]);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      setSearching(true);
      try { setResults(await searchPhotographersForReply(search)); }
      finally { setSearching(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  async function togglePick(p: PhotographerPick) {
    if (selectedSet.has(p.id)) {
      setSelectedIds(selectedIds.filter((id) => id !== p.id));
      setPicked(picked.filter((x) => x.id !== p.id));
    } else {
      setSelectedIds([...selectedIds, p.id]);
      setPicked([...picked, p]);
    }
  }

  async function send() {
    setError(null); setOk(null);
    if (body.trim().length < 10) { setError("Bitte schreibe eine Antwort (min. 10 Zeichen)."); return; }
    setSending(true);
    try {
      const full = picked.length === selectedIds.length
        ? picked
        : await getPhotographersByIds(selectedIds);
      const reply = await createBriefReply({
        brief_id: brief.id,
        recipient_email: brief.contact_email,
        body,
        photographer_ids: selectedIds,
      });
      await sendTransactionalEmail({
        templateName: "brief-reply",
        recipientEmail: brief.contact_email,
        idempotencyKey: `brief-reply-${reply.id}`,
        templateData: {
          message: body,
          occasion: brief.occasion,
          photographers: full.map((p) => ({
            name: p.name,
            region: p.region,
            portraitUrl: p.portrait_url,
            slug: p.slug,
          })),
        },
      });
      setOk("Antwort gesendet.");
      setBody(""); setSelectedIds([]); setPicked([]);
      await onSent();
    } catch (e: any) {
      setError(e?.message ?? "Fehler beim Senden");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mt-6 space-y-6">
      <div>
        <div className="eyebrow text-muted-foreground mb-2">An</div>
        <div className="text-sm">{brief.contact_name ? `${brief.contact_name} <${brief.contact_email}>` : brief.contact_email}</div>
      </div>

      <div>
        <div className="eyebrow text-muted-foreground mb-2">Nachricht</div>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6}
          placeholder="Hallo, vielen Dank für deine Anfrage …"
          className="w-full border border-border bg-transparent p-3 text-sm" maxLength={5000} />
      </div>

      <div>
        <div className="eyebrow text-muted-foreground mb-2">Fotograf-Bausteine ({selectedIds.length})</div>
        {picked.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {picked.map((p) => (
              <button key={p.id} onClick={() => togglePick(p)}
                className="flex items-center gap-2 border border-foreground bg-foreground text-background pl-1 pr-2 py-1 text-xs">
                {p.portrait_url
                  ? <img src={p.portrait_url} alt="" className="w-6 h-6 object-cover" />
                  : <span className="w-6 h-6 bg-muted" />}
                <span>{p.name}</span>
                <span className="opacity-60">×</span>
              </button>
            ))}
          </div>
        )}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Fotograf suchen …"
          className="w-full border-b border-foreground bg-transparent py-2 outline-none text-sm" />
        <div className="mt-2 border border-border max-h-64 overflow-y-auto divide-y divide-border">
          {searching && <div className="p-3 text-xs text-muted-foreground">Suche …</div>}
          {!searching && results.length === 0 && <div className="p-3 text-xs text-muted-foreground">Keine Treffer.</div>}
          {results.map((p) => {
            const active = selectedSet.has(p.id);
            return (
              <button key={p.id} onClick={() => togglePick(p)}
                className={`w-full text-left p-2 flex items-center gap-3 hover:bg-muted/40 ${active ? "bg-muted/60" : ""}`}>
                {p.portrait_url
                  ? <img src={p.portrait_url} alt="" className="w-10 h-10 object-cover" />
                  : <span className="w-10 h-10 bg-muted block" />}
                <div className="min-w-0">
                  <div className="text-sm truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{p.region ?? ""}</div>
                </div>
                <span className={`ml-auto eyebrow text-xs ${active ? "text-foreground" : "text-muted-foreground"}`}>
                  {active ? "✓ gewählt" : "+ hinzufügen"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {error && <div className="text-sm text-destructive">{error}</div>}
      {ok && <div className="text-sm text-foreground">{ok}</div>}

      <div className="flex items-center gap-3">
        <button disabled={sending} onClick={send}
          className="eyebrow bg-foreground text-background px-4 py-2 disabled:opacity-50">
          {sending ? "Sendet …" : "Antwort senden"}
        </button>
      </div>

      {replies.length > 0 && (
        <div>
          <div className="eyebrow text-muted-foreground mb-3">Verlauf</div>
          <div className="space-y-3">
            {replies.map((r) => (
              <div key={r.id} className="border border-border p-3">
                <div className="text-xs text-muted-foreground mb-2">
                  {new Date(r.sent_at).toLocaleString()} · an {r.recipient_email} · {r.photographer_ids.length} Fotografen
                </div>
                <div className="text-sm whitespace-pre-wrap">{r.body}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <dt className="eyebrow text-muted-foreground col-span-1">{label}</dt>
      <dd className="col-span-2">{children}</dd>
    </div>
  );
}